import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, Button, Select } from '@react-apps/design-system';
import { Textarea } from '@/shared/components/textarea';
import type {
  PendingAlertItem,
  PendingSourceActions,
} from '../../domain/entities/pending-alert.entity';
import {
  ErrorBanner,
  SuccessBanner,
  getApiErrorMessage,
} from '../shared/display';

/**
 * Mesma política de anexo do Sales (`justification-fields.tsx`, PDF/JPEG/PNG
 * até 20MB) — reaplicada aqui porque a Central de Pendências agrega fontes
 * que não conhecem a política de upload umas das outras. Só entra em jogo
 * quando `actions.uploadAttachment` existe (hoje só Vendas).
 */
const ACCEPTED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
];
const MAX_SIZE_MB = 20;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

function validateAttachment(file: File): string | null {
  if (!ACCEPTED_TYPES.includes(file.type)) {
    return 'Formato inválido. Apenas PDF, JPG ou PNG são aceitos.';
  }
  if (file.size > MAX_SIZE_BYTES) {
    return `Arquivo muito grande. O tamanho máximo é ${MAX_SIZE_MB}MB.`;
  }
  return null;
}

/**
 * Confirma resolver/descartar um item da Central de Pendências — a
 * justificativa (obrigatória) e o anexo (opcional, só quando a fonte suporta)
 * são o "modal de confirmação": nada é executado sem esse passo explícito.
 *
 * "Não abrir de novo para esta <entidade>" só aparece quando o item aceita
 * (`closableForEntity`), e nasce **desmarcado**: marcado por engano, é silêncio
 * permanente sobre aquela entidade até alguém desfazer na tela dela.
 */
export function ClosePendingAlertModal({
  item,
  actions,
  open,
  onClose,
}: {
  item: PendingAlertItem;
  actions: PendingSourceActions;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [resolved, setResolved] = useState(true);
  const [note, setNote] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [rejectedFile, setRejectedFile] = useState(false);
  const [closeForEntity, setCloseForEntity] = useState(false);

  const mutation = useMutation({
    mutationFn: async () => {
      let attachmentFileId: string | undefined;
      if (file && actions.uploadAttachment) {
        const result = await actions.uploadAttachment(file);
        attachmentFileId = result.fileId;
      }
      await actions.close(item.id, resolved, {
        note: note.trim(),
        attachmentFileId,
        closeForEntity: item.closableForEntity ? closeForEntity : undefined,
      });
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['pending-alerts'] }),
  });

  useEffect(() => {
    if (open) {
      setResolved(true);
      setNote('');
      setFile(null);
      setAttachmentError(null);
      setRejectedFile(false);
      setCloseForEntity(false);
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectFile = useCallback((selected: File | null) => {
    if (!selected) {
      setFile(null);
      setRejectedFile(false);
      setAttachmentError(null);
      return;
    }
    const error = validateAttachment(selected);
    setAttachmentError(error);
    setRejectedFile(error !== null);
    setFile(error === null ? selected : null);
  }, []);

  const done = mutation.isSuccess;
  const isIncomplete = note.trim() === '' || rejectedFile;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Fechar pendência"
      description={`${item.sourceLabel} · ${item.typeLabel} — sai da fila só por decisão humana.`}
      size="md"
      footer={
        done ? (
          <Button onClick={onClose}>Fechar</Button>
        ) : (
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              Voltar
            </Button>
            <Button
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending || isIncomplete}
            >
              {mutation.isPending
                ? 'Registrando…'
                : resolved
                  ? 'Marcar como resolvida'
                  : 'Descartar'}
            </Button>
          </div>
        )
      }
    >
      <div className="space-y-4">
        {done && (
          <SuccessBanner
            message={
              resolved
                ? 'Pendência marcada como resolvida.'
                : 'Pendência descartada.'
            }
          />
        )}
        {mutation.isError && (
          <ErrorBanner
            message={getApiErrorMessage(
              mutation.error,
              'Falha ao fechar a pendência.',
            )}
          />
        )}
        {!done && (
          <>
            <Select
              label="Desfecho"
              id="close-pending-alert-outcome"
              options={[
                { value: 'resolved', label: 'Resolvida — foi endereçada' },
                {
                  value: 'dismissed',
                  label: 'Descartada — falso positivo ou nada a fazer',
                },
              ]}
              value={resolved ? 'resolved' : 'dismissed'}
              onChange={(e) => setResolved(e.target.value === 'resolved')}
            />
            <Textarea
              label="Justificativa (obrigatória)"
              value={note}
              onChange={setNote}
              rows={3}
              helperText="Fica registrada no histórico, junto com quem executou."
            />
            {item.closableForEntity && (
              <div className="flex flex-col gap-1">
                <label className="text-(--color-text-primary) flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={closeForEntity}
                    disabled={mutation.isPending}
                    onChange={(e) => setCloseForEntity(e.target.checked)}
                  />
                  Não abrir de novo para {item.entityTypeLabel.toLowerCase()}{' '}
                  {item.entityId}
                </label>
                <p className="text-(--color-text-secondary) text-xs">
                  Para quando o caso já foi acertado por fora e só seria
                  detectado de novo. Uma ocorrência nova nela também deixa de
                  abrir alerta — desfaz-se na própria{' '}
                  {item.entityTypeLabel.toLowerCase()}.
                </p>
              </div>
            )}
            {actions.uploadAttachment && (
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="pending-alert-close-attachment"
                  className="text-(--color-text-primary) text-sm font-medium"
                >
                  Anexo (opcional)
                </label>
                <input
                  id="pending-alert-close-attachment"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  disabled={mutation.isPending}
                  onChange={(e) => selectFile(e.target.files?.[0] ?? null)}
                  className="text-(--color-text-secondary) file:border-(--color-border) file:bg-(--color-bg-secondary) file:text-(--color-text-primary) hover:file:bg-(--color-bg-primary) w-full text-sm file:mr-3 file:cursor-pointer file:rounded-lg file:border file:px-3 file:py-1.5 file:text-sm"
                />
                <p className="text-(--color-text-secondary) text-xs">
                  Comprovante do que sustentar a decisão. PDF, JPG ou PNG, até{' '}
                  {MAX_SIZE_MB}MB.
                </p>
                {attachmentError && (
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {attachmentError}
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
